import { DataStorePaneState } from './DataStorePaneState';
import {
  DataStoreValue,
  getMarkIndicesForPane,
  getPaneIndicesForMarkIdsInPane,
  getXGridLineIndicesForMarkIdsInPane,
  getYGridLineIndicesForMarkIdsInPane,
  getUserPaneRowIndicesForMarkIdsInPane,
  getUserPaneColumnIndicesForMarkIdsInPane,
  attributesNearlyEqual,
  marksNearlyEqual,
  labelsNearlyEqual,
} from './DataStorePaneStateUtility';
import { MarkAllAnimationParams } from './MarkAllAnimationParams';
import { MarkAnimationParams } from './MarkAnimationParams';
import { FractionWarperCurve, MarkAnimationTiming } from './MarkAnimationTiming';
import { PaneStateAttribute, PaneStateAttributeUtil } from './PaneStateAttribute';

const COMPARISON_EPSILON = 1e-5;

/**
 * Resolves, culls, and normalizes animation timing for a pane.
 * Port of C++ MarkAllAnimationParamsTimingResolver.
 *
 * Pipeline: CullAnimationsWithNoMarks → CullNoopAnimations → RemoveEmptyGaps → ComputeEndTime → NormalizeTimings.
 *
 * C++ Reference: tabanim/main/sceneanimation/MarkAllAnimationParamsTimingResolver.cpp
 */
export class MarkAllAnimationParamsTimingResolver {
  private static zeroOutOverallTiming(params: MarkAnimationParams): void {
    const timing = params.overallTiming.clone();
    timing.delay = 0;
    timing.duration = 0;
    params.overallTiming = timing;
  }

  /**
   * Zero out timing for animation phases that have no marks.
   * C++ Reference: CullAnimationsWithNoMarks()
   */
  static cullAnimationsWithNoMarks(
    params: MarkAllAnimationParams,
    enterMarkIds: number[],
    moveMarkIds: number[],
    exitMarkIds: number[]
  ): MarkAllAnimationParams {
    const zero = new MarkAnimationTiming(0, 0, FractionWarperCurve.Linear);
    if (enterMarkIds.length === 0) {
      params.enterParams.overallTiming = zero.clone();
      params.enterParams.clearAllAttributeOverrideTiming();
    }
    if (moveMarkIds.length === 0) {
      params.moveParams.overallTiming = zero.clone();
      params.moveParams.clearAllAttributeOverrideTiming();
    }
    if (exitMarkIds.length === 0) {
      params.exitParams.overallTiming = zero.clone();
      params.exitParams.clearAllAttributeOverrideTiming();
    }
    return params;
  }

  private static cullEnterNoopsForMarks(
    params: MarkAnimationParams,
    paneId: string,
    markIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (endState === null) {
      this.zeroOutOverallTiming(params);
      params.clearAllAttributeOverrideTiming();
      return;
    }
    this.cullNoopsForMarks(params, paneId, markIds, startState, endState);
  }

  private static cullMoveNoopsForMarks(
    params: MarkAnimationParams,
    paneId: string,
    markIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (startState === null || endState === null) {
      this.zeroOutOverallTiming(params);
      params.clearAllAttributeOverrideTiming();
      return;
    }
    this.cullNoopsForMarks(params, paneId, markIds, startState, endState);
  }

  private static cullExitNoopsForMarks(
    params: MarkAnimationParams,
    paneId: string,
    markIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (startState === null || endState === null) {
      this.zeroOutOverallTiming(params);
      params.clearAllAttributeOverrideTiming();
      return;
    }
    this.cullNoopsForMarks(params, paneId, markIds, startState, endState);
  }

  private static cullNoopsForMarks(
    params: MarkAnimationParams,
    paneId: string,
    markIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (startState === null || endState === null) {
      return;
    }
    const labelAttributes = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getAllLabelAttributes());
    const overrideAttributes = new Set<PaneStateAttribute>();
    params.attributeTimingOverrideMap.forEach((_timing, attr) => {
      overrideAttributes.add(attr as PaneStateAttribute);
    });
    const overallAttributes = PaneStateAttributeUtil.getAllMarkAttributes().filter((attribute) => !overrideAttributes.has(attribute));
    if (
      params.overallTiming.duration > 0 &&
      marksNearlyEqual(
        startState.getValue.bind(startState) as (path: string) => DataStoreValue,
        endState.getValue.bind(endState) as (path: string) => DataStoreValue,
        paneId,
        markIds,
        overallAttributes,
        COMPARISON_EPSILON
      )
    ) {
      this.zeroOutOverallTiming(params);
    }
    const cullAttributes = new Set<PaneStateAttribute>();
    params.attributeTimingOverrideMap.forEach((timing, attr) => {
      const attribute = attr as PaneStateAttribute;
      if (labelAttributes.has(attribute)) {
        return;
      }
      if (
        timing.duration <= 0 ||
        marksNearlyEqual(
          startState.getValue.bind(startState) as (path: string) => DataStoreValue,
          endState.getValue.bind(endState) as (path: string) => DataStoreValue,
          paneId,
          markIds,
          [attribute],
          COMPARISON_EPSILON
        )
      ) {
        cullAttributes.add(attribute);
      }
    });
    cullAttributes.forEach((attribute) => {
      params.clearAttributeOverrideTiming(attribute);
    });
  }

  private static cullOverrideNoopsForLabels(
    params: MarkAnimationParams,
    paneId: string,
    markIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (startState === null || endState === null) {
      return;
    }
    const labelAttributes = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getAllLabelAttributes());
    const cullAttributes = new Set<PaneStateAttribute>();
    params.attributeTimingOverrideMap.forEach((timing, attr) => {
      const attribute = attr as PaneStateAttribute;
      if (!labelAttributes.has(attribute)) {
        return;
      }
      if (
        timing.duration <= 0 ||
        labelsNearlyEqual(
          startState.getValue.bind(startState) as (path: string) => DataStoreValue,
          endState.getValue.bind(endState) as (path: string) => DataStoreValue,
          paneId,
          markIds,
          [attribute],
          COMPARISON_EPSILON
        )
      ) {
        cullAttributes.add(attribute);
      }
    });
    cullAttributes.forEach((attribute) => {
      params.clearAttributeOverrideTiming(attribute);
    });
  }

  private static panesDifferForAnimation(
    paneId: string,
    startState: DataStorePaneState,
    endState: DataStorePaneState,
    attributes: PaneStateAttribute[],
    movingPaneSampleMarkIds: number[]
  ): boolean {
    const startIndices = getPaneIndicesForMarkIdsInPane(
      paneId,
      movingPaneSampleMarkIds,
      startState.buildPaneIdMarkIdToPaneIndexMap.bind(startState) as (paneId: string, outMap: Map<string, number>) => void
    );
    const endIndices = getPaneIndicesForMarkIdsInPane(
      paneId,
      movingPaneSampleMarkIds,
      endState.buildPaneIdMarkIdToPaneIndexMap.bind(endState) as (paneId: string, outMap: Map<string, number>) => void
    );
    return !attributesNearlyEqual(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      attributes,
      COMPARISON_EPSILON,
      '',
      startIndices,
      endIndices
    );
  }

  private static gridsDifferForAnimation(
    paneId: string,
    startState: DataStorePaneState,
    endState: DataStorePaneState,
    attributes: PaneStateAttribute[],
    movingPaneSampleMarkIds: number[]
  ): boolean {
    const startXIndices = getXGridLineIndicesForMarkIdsInPane(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      paneId,
      movingPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(startState.getValue.bind(startState) as (path: string) => DataStoreValue, id, ids)
    );
    const endXIndices = getXGridLineIndicesForMarkIdsInPane(
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      paneId,
      movingPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(endState.getValue.bind(endState) as (path: string) => DataStoreValue, id, ids)
    );
    const startYIndices = getYGridLineIndicesForMarkIdsInPane(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      paneId,
      movingPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(startState.getValue.bind(startState) as (path: string) => DataStoreValue, id, ids),
      startState.getNumPaneColumns.bind(startState) as () => number
    );
    const endYIndices = getYGridLineIndicesForMarkIdsInPane(
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      paneId,
      movingPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(endState.getValue.bind(endState) as (path: string) => DataStoreValue, id, ids),
      endState.getNumPaneColumns.bind(endState) as () => number
    );
    return (
      !attributesNearlyEqual(
        startState.getValue.bind(startState) as (path: string) => DataStoreValue,
        endState.getValue.bind(endState) as (path: string) => DataStoreValue,
        attributes,
        COMPARISON_EPSILON,
        '',
        startXIndices,
        endXIndices
      ) ||
      !attributesNearlyEqual(
        startState.getValue.bind(startState) as (path: string) => DataStoreValue,
        endState.getValue.bind(endState) as (path: string) => DataStoreValue,
        attributes,
        COMPARISON_EPSILON,
        '',
        startYIndices,
        endYIndices
      )
    );
  }

  private static userPanesDifferForAnimation(
    paneId: string,
    startState: DataStorePaneState,
    endState: DataStorePaneState,
    attributes: PaneStateAttribute[],
    movingUserPaneSampleMarkIds: number[]
  ): boolean {
    const startRowIndices = getUserPaneRowIndicesForMarkIdsInPane(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      paneId,
      movingUserPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(startState.getValue.bind(startState) as (path: string) => DataStoreValue, id, ids)
    );
    const endRowIndices = getUserPaneRowIndicesForMarkIdsInPane(
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      paneId,
      movingUserPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(endState.getValue.bind(endState) as (path: string) => DataStoreValue, id, ids)
    );
    const startColumnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      paneId,
      movingUserPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(startState.getValue.bind(startState) as (path: string) => DataStoreValue, id, ids)
    );
    const endColumnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      paneId,
      movingUserPaneSampleMarkIds,
      (id, ids) => getMarkIndicesForPane(endState.getValue.bind(endState) as (path: string) => DataStoreValue, id, ids)
    );
    return (
      !attributesNearlyEqual(
        startState.getValue.bind(startState) as (path: string) => DataStoreValue,
        endState.getValue.bind(endState) as (path: string) => DataStoreValue,
        attributes,
        COMPARISON_EPSILON,
        '',
        startRowIndices,
        endRowIndices
      ) ||
      !attributesNearlyEqual(
        startState.getValue.bind(startState) as (path: string) => DataStoreValue,
        endState.getValue.bind(endState) as (path: string) => DataStoreValue,
        attributes,
        COMPARISON_EPSILON,
        '',
        startColumnIndices,
        endColumnIndices
      )
    );
  }

  private static heatmapsDifferForAnimation(
    paneId: string,
    startState: DataStorePaneState,
    endState: DataStorePaneState,
    attributes: PaneStateAttribute[]
  ): boolean {
    return !attributesNearlyEqual(
      startState.getValue.bind(startState) as (path: string) => DataStoreValue,
      endState.getValue.bind(endState) as (path: string) => DataStoreValue,
      attributes,
      COMPARISON_EPSILON,
      paneId
    );
  }

  private static cullNoopsForNonMarks(
    params: MarkAnimationParams,
    paneId: string,
    enterMarkIds: number[],
    moveMarkIds: number[],
    movingUserPaneSampleMarkIds: number[],
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null
  ): void {
    if (startState === null || endState === null) {
      this.zeroOutOverallTiming(params);
      params.clearAllAttributeOverrideTiming();
      return;
    }
    if (enterMarkIds.length > 0) {
      return;
    }

    let paneAttributes = PaneStateAttributeUtil.getAllPaneBoundsAttributes();
    let gridAttributes = PaneStateAttributeUtil.getGridPerLineAttributes();
    let userPaneAttributes = PaneStateAttributeUtil.getAllUserPaneAttributes();
    const heatmapAttributes = PaneStateAttributeUtil.getAllHeatmapNonMarkAttributes();
    const paneAttributeSet = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getAllPaneBoundsAttributes());
    const gridAttributeSet = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getGridPerLineAttributes());
    const userPaneAttributeSet = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getAllUserPaneAttributes());
    const heatmapAttributeSet = new Set<PaneStateAttribute>(PaneStateAttributeUtil.getAllHeatmapNonMarkAttributes());

    params.attributeTimingOverrideMap.forEach((_timing, attr) => {
      const attribute = attr as PaneStateAttribute;
      paneAttributes = paneAttributes.filter((candidate) => candidate !== attribute);
      gridAttributes = gridAttributes.filter((candidate) => candidate !== attribute);
      userPaneAttributes = userPaneAttributes.filter((candidate) => candidate !== attribute);
    });

    if (
      params.overallTiming.duration > 0 &&
      !this.panesDifferForAnimation(paneId, startState, endState, paneAttributes, moveMarkIds) &&
      !this.gridsDifferForAnimation(paneId, startState, endState, gridAttributes, moveMarkIds) &&
      !this.userPanesDifferForAnimation(paneId, startState, endState, userPaneAttributes, movingUserPaneSampleMarkIds) &&
      !this.heatmapsDifferForAnimation(paneId, startState, endState, heatmapAttributes)
    ) {
      this.zeroOutOverallTiming(params);
    }

    const cullAttributes = new Set<PaneStateAttribute>();
    params.attributeTimingOverrideMap.forEach((timing, attr) => {
      const attribute = attr as PaneStateAttribute;
      let cullIt = timing.duration <= 0;
      if (!cullIt) {
        if (paneAttributeSet.has(attribute)) {
          cullIt = !this.panesDifferForAnimation(paneId, startState, endState, [attribute], moveMarkIds);
        } else if (gridAttributeSet.has(attribute)) {
          cullIt = !this.gridsDifferForAnimation(paneId, startState, endState, [attribute], moveMarkIds);
        } else if (userPaneAttributeSet.has(attribute)) {
          cullIt = !this.userPanesDifferForAnimation(paneId, startState, endState, [attribute], movingUserPaneSampleMarkIds);
        } else if (heatmapAttributeSet.has(attribute)) {
          cullIt = !this.heatmapsDifferForAnimation(paneId, startState, endState, [attribute]);
        }
      }
      if (cullIt) {
        cullAttributes.add(attribute);
      }
    });
    cullAttributes.forEach((attribute) => {
      params.clearAttributeOverrideTiming(attribute);
    });
  }

  static cullNoopAnimations(
    params: MarkAllAnimationParams,
    paneId: string,
    startState: DataStorePaneState | null,
    endState: DataStorePaneState | null,
    enterMarkIds: number[],
    moveMarkIds: number[],
    moveLabelMarkIds: number[],
    exitMarkIds: number[],
    movingUserPaneSampleMarkIds: number[]
  ): MarkAllAnimationParams {
    this.cullEnterNoopsForMarks(params.enterParams, paneId, enterMarkIds, startState, endState);
    this.cullMoveNoopsForMarks(params.moveParams, paneId, moveMarkIds, startState, endState);
    this.cullOverrideNoopsForLabels(params.moveParams, paneId, moveLabelMarkIds, startState, endState);
    this.cullExitNoopsForMarks(params.exitParams, paneId, exitMarkIds, startState, endState);
    this.cullNoopsForNonMarks(params.nonMarksParams, paneId, enterMarkIds, moveMarkIds, movingUserPaneSampleMarkIds, startState, endState);
    return params;
  }

  /**
   * Collect all start/end event times from a params object.
   * Returns array of [time, isStart] pairs.
   * C++ Reference: CollectAnimStartEndTimes()
   */
  private static collectAnimStartEndTimes(params: MarkAllAnimationParams): Array<[number, boolean]> {
    const result: Array<[number, boolean]> = [];
    params.allFourParams().forEach((p: MarkAnimationParams) => {
      const t = p.overallTiming;
      result.push([t.delay, true]);
      result.push([t.getEnd(), false]);
      p.attributeTimingOverrideMap.forEach((attrTiming: MarkAnimationTiming) => {
        result.push([attrTiming.delay, true]);
        result.push([attrTiming.getEnd(), false]);
      });
    });
    return result;
  }

  private static computeEmptyGapsFromEvents(events: Array<[number, boolean]>): Array<[number, number]> {
    events.sort((a, b) => (a[0] !== b[0] ? a[0] - b[0] : a[1] === b[1] ? 0 : a[1] ? -1 : 1));
    const gaps: Array<[number, number]> = [];
    let numActive = 0;
    let prevTime = 0;
    events.forEach(([time, isStart]) => {
      if (time - prevTime > COMPARISON_EPSILON && numActive <= 0) {
        gaps.push([prevTime, time]);
      }
      prevTime = time;
      numActive += isStart ? 1 : -1;
    });
    return gaps;
  }

  /**
   * Find gaps in the animation timeline where nothing is animating.
   * C++ Reference: ComputeEmptyGaps()
   */
  static computeEmptyGaps(params: MarkAllAnimationParams): Array<[number, number]> {
    return this.computeEmptyGapsFromEvents(this.collectAnimStartEndTimes(params));
  }

  private static removeEmptyGapsWithRanges(params: MarkAllAnimationParams, gaps: Array<[number, number]>): MarkAllAnimationParams {
    if (gaps.length === 0) return params;

    const thresholds: Array<[number, number]> = [];
    let cumulative = 0;
    gaps.forEach(([gapStart, gapEnd]) => {
      cumulative += gapEnd - gapStart;
      thresholds.push([gapStart, cumulative]);
    });

    const adjustDelay = (delay: number): number => {
      for (let i = thresholds.length - 1; i >= 0; i--) {
        if (delay >= thresholds[i][0]) {
          return delay - thresholds[i][1];
        }
      }
      return delay;
    };

    const adjustParam = (p: MarkAnimationParams): void => {
      if (p.overallTiming.duration > 0) {
        p.overallTiming.delay = adjustDelay(p.overallTiming.delay);
      }
      p.attributeTimingOverrideMap.forEach((t: MarkAnimationTiming, attr: string) => {
        const adjusted = t.clone();
        adjusted.delay = adjustDelay(t.delay);
        p.attributeTimingOverrideMap.set(attr, adjusted);
      });
    };

    params.allFourParams().forEach(adjustParam);
    return params;
  }

  /**
   * Shift delays backward to close any empty gaps in the timeline.
   * C++ Reference: RemoveEmptyGaps()
   */
  static removeEmptyGaps(params: MarkAllAnimationParams): MarkAllAnimationParams {
    return this.removeEmptyGapsWithRanges(params, this.computeEmptyGaps(params));
  }

  static removeEmptyGapsForAll(paramsVector: MarkAllAnimationParams[]): MarkAllAnimationParams[] {
    if (paramsVector.length === 0) {
      return [];
    }
    const allEvents: Array<[number, boolean]> = [];
    paramsVector.forEach((params) => {
      allEvents.push(...this.collectAnimStartEndTimes(params));
    });
    const gaps = this.computeEmptyGapsFromEvents(allEvents);
    return paramsVector.map((params) => this.removeEmptyGapsWithRanges(params, gaps));
  }

  /**
   * Find the latest end time across all timing slots.
   * C++ Reference: ComputeEndTime()
   */
  static computeEndTime(params: MarkAllAnimationParams): number {
    let result = 0;
    params.allFourParams().forEach((p: MarkAnimationParams) => {
      result = Math.max(result, p.overallTiming.getEnd());
      p.attributeTimingOverrideMap.forEach((t: MarkAnimationTiming) => {
        result = Math.max(result, t.getEnd());
      });
    });
    return result;
  }

  /**
   * Normalize all timings so that the total duration equals 1.0.
   * Mirrors C++ NormalizeTimings() exactly: only normalizes when duration > 0;
   * treats delay <= 0 as normalizedStart = 0, end <= 0 as normalizedEnd = 0.
   * C++ Reference: NormalizeTimings()
   */
  static normalizeTimings(params: MarkAllAnimationParams, totalDuration: number): MarkAllAnimationParams {
    if (totalDuration <= 0) return params;

    const normalizeOne = (t: MarkAnimationTiming): void => {
      if (t.duration > 0) {
        let normalizedStart = 0;
        let normalizedEnd = 0;
        if (t.delay > 0) {
          normalizedStart = t.delay / totalDuration;
        }
        if (t.getEnd() > 0) {
          normalizedEnd = t.getEnd() / totalDuration;
        }
        t.delay = normalizedStart;
        t.duration = normalizedEnd - normalizedStart;
      }
    };

    params.allFourParams().forEach((p: MarkAnimationParams) => {
      normalizeOne(p.overallTiming);
      p.attributeTimingOverrideMap.forEach((t: MarkAnimationTiming) => {
        normalizeOne(t);
      });
    });
    return params;
  }

  /**
   * Full pipeline: cull → remove gaps → compute total duration → normalize.
   * Returns [resolvedParams, totalDurationInSlots].
   *
   * C++ Reference: ResolveAndNormalizeParamsTiming()
   */
  static resolveAndNormalizeParamsTiming(
    params: MarkAllAnimationParams,
    enterMarkIds: number[],
    moveMarkIds: number[],
    exitMarkIds: number[]
  ): [MarkAllAnimationParams, number] {
    let result = this.cullAnimationsWithNoMarks(params, enterMarkIds, moveMarkIds, exitMarkIds);
    result = this.removeEmptyGaps(result);
    const duration = this.computeEndTime(result);
    result = this.normalizeTimings(result, duration);
    return [result, duration];
  }
}
